import { FC, useEffect, useState } from 'react';
import { getOnFarmEffectivePricePlan } from '../../../api/onFarmBillingOverrideApi';
import { getOnFarmStorageRates } from '../../../api/onFarmStorageRatesApi';
import { Loader } from '../../../components';
import { ON_FARM_BILLING_CONSTANTS } from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { useAppSelector } from '../../../redux/hooks';
import { centsToDollars } from '../../../services/account.service';
import { showToast } from '../../../services/toast.service';
import { OnFarmEffectivePricePlan } from '../../../types/interfaces/onFarmBillingOverride.interfaces';
import { OnFarmStorageRatesResponse } from '../../../types/interfaces/onFarmStorageRates.interfaces';

type PlanCardProps = {
  title: string;
  price: string;
  priceCaption?: string;
  highlights: string[];
  description: string;
  variant: 'featured' | 'quote';
};

const PlanCard: FC<PlanCardProps> = ({ title, price, priceCaption, highlights, description, variant }) => (
  <article className={`on-farm-plan-card on-farm-plan-card--${variant}`}>
    <h3>{title}</h3>
    <div className="on-farm-plan-card__price">
      <span className="on-farm-plan-card__amount">{price}</span>
      {priceCaption && <span className="on-farm-plan-card__caption">{priceCaption}</span>}
    </div>
    <ul className="on-farm-plan-card__highlights">
      {highlights.map(highlight => (
        <li key={highlight}>{highlight}</li>
      ))}
    </ul>
    <p className="on-farm-plan-card__description">{description}</p>
  </article>
);

const OnFarmStorageRates: FC = () => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const [rates, setRates] = useState<OnFarmStorageRatesResponse | null>(null);
  const [activePlan, setActivePlan] = useState<OnFarmEffectivePricePlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const copy = ON_FARM_BILLING_CONSTANTS;

  useEffect(() => {
    const loadStorageRates = async () => {
      if (!accountId) {
        setRates(null);
        setActivePlan(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const { data } = await getOnFarmStorageRates();
        setRates(data);
        try {
          const { data: plan } = await getOnFarmEffectivePricePlan(accountId);
          setActivePlan(plan);
        } catch {
          setActivePlan(null);
        }
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setRates(null);
        setActivePlan(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadStorageRates();
  }, [accountId]);

  if (isLoading) {
    return (
      <div className="on-farm-storage-rates">
        <Loader loaderSize="small" />
      </div>
    );
  }

  const maxTanks = rates?.maxStandardTanks;
  const includedUnits = rates?.includedUnitsPerTank;
  const monthlyFeeCents = rates?.flatMonthlyCents;
  const isCustomQuote = Boolean(activePlan?.isCustomQuote);
  const activeMaxTanks = activePlan?.maxTanks;
  const activeMonthlyFeeCents = activePlan?.flatMonthlyCents;

  return (
    <div className="on-farm-storage-rates">
      <p className="on-farm-storage-rates__intro">{copy.SUBHEADER}</p>

      {activePlan && (
        <section className="on-farm-active-plan">
          <div className="on-farm-active-plan__identity">
            <p className="on-farm-active-plan__label">{copy.ACTIVE_PLAN}</p>
            <div className="on-farm-active-plan__title">
              <h3>{isCustomQuote ? copy.CUSTOM_PLAN : copy.STANDARD_PLAN}</h3>
              <span className="on-farm-active-plan__badge">{copy.ACTIVE}</span>
            </div>
          </div>
          <div className="on-farm-active-plan__stats">
            <div>
              <span>{copy.MONTHLY_FEE}</span>
              <strong>{`$${centsToDollars(activeMonthlyFeeCents)}`}</strong>
            </div>
            <div>
              <span>{copy.MAX_TANKS}</span>
              <strong>{activeMaxTanks ?? copy.NOT_PROVIDED}</strong>
            </div>
            <div>
              <span>{copy.UNITS}</span>
              <strong>{activePlan.includedUnitsPerTank ?? copy.NOT_PROVIDED}</strong>
            </div>
          </div>
        </section>
      )}

      <div className="on-farm-plan-grid">
        <PlanCard
          title={copy.STANDARD_PLAN}
          price={`$${centsToDollars(monthlyFeeCents)}`}
          priceCaption={copy.PER_MONTH}
          highlights={[
            ...(maxTanks != null ? [copy.UP_TO_TANKS(maxTanks)] : []),
            ...(includedUnits != null ? [copy.UNITS_PER_TANK(includedUnits)] : []),
            copy.FLAT_RATE,
          ]}
          description={
            maxTanks != null && includedUnits != null ? copy.STANDARD_DESCRIPTION(maxTanks, includedUnits) : copy.SUBHEADER
          }
          variant="featured"
        />
        {maxTanks != null && (
          <PlanCard
            title={copy.CUSTOM_QUOTE_TITLE(maxTanks)}
            price={copy.CUSTOM_QUOTE}
            highlights={includedUnits != null ? [copy.UNITS_PER_TANK(includedUnits)] : []}
            description={copy.CUSTOM_QUOTE_DESCRIPTION}
            variant="quote"
          />
        )}
      </div>
    </div>
  );
};

export default OnFarmStorageRates;
